import React, { useEffect, useState } from 'react';
import { Col, convertToKRW, getItemWithExpiration, Row } from '../../lib/utils';
import dayjs, { Dayjs } from 'dayjs';
import { DemiLightText, MediumText, RegularText } from '../../lib/Text';
import SvgIcon from '../../components/SvgIcon';
import { colors } from '../../lib/colors';
import Dropdown from '../../components/DropDown/DropDown';
import {
    MembershipPaymentData,
    SalesDataResponse,
    SalesListSummaryData,
    SalesManager,
} from '../../services/useMembershipPaymentsQueryService';
import MemberSearchTextInput from '../../components/Button/MemberSearchTextInput';
import { createColumnHelper } from '@tanstack/react-table';
import Table from '../../components/Table/Table';
import * as XLSX from 'xlsx'; // 엑셀 변환 라이브러리
import { saveAs } from 'file-saver'; // 파일 저장 라이브러리
import { TooltipIcon } from './SettlementCalendar';
import { CodeValueType } from '../../types/types';
import NewCalendar from '../../components/Calendar/NewCalendar';
import { attendanceStatusData, memberStatusData } from '../../lib/varialbe';
import SalesRecordDetailModal from './SalesRecordDetailModal';
import { getSalesRefundBlockMessage } from './salesRecordDetail';
import {
    buildCoachFilterOptions,
    COACH_FILTER_ALL,
    isMatchingCoachFilter,
    resolveCoachFilterSelection,
} from './salesListCoachFilter';

type Props = {
    createAlert?: (title: string, content: string, isError: boolean) => void;
    date: string;
    onMonthNavigate: (date: Date | string | Dayjs) => void;
    salesListData: SalesDataResponse;
    selectedSalesManager: SalesManager;
    onSalesManagerSelect: (salesManager: CodeValueType) => void;
    selectedSalesListDate: Dayjs | string | undefined;
    onChangeSelectedSalesListDate: (date: Dayjs | string | undefined) => void;
    onRefetchSalesRecord: (record: MembershipPaymentData) => Promise<MembershipPaymentData>;
};
const columnHelper = createColumnHelper<MembershipPaymentData>();

const buildMembershipTemplateOptions = (
    filterList?: Array<{ membershipTemplateId: string; membershipTemplateName: string }>,
    tableRows?: MembershipPaymentData[],
) => {
    const optionMap = new Map<string, { code: string; value: string }>();

    (filterList ?? []).forEach((item) => {
        if (!item.membershipTemplateId || !item.membershipTemplateName) return;
        optionMap.set(`id:${item.membershipTemplateId}`, {
            code: `id:${item.membershipTemplateId}`,
            value: item.membershipTemplateName,
        });
    });

    (tableRows ?? []).forEach((item) => {
        const membershipTemplateId = item.membershipTemplateId?.trim();
        const membershipName = item.membershipName?.trim();

        if (membershipTemplateId) {
            const key = `id:${membershipTemplateId}`;
            if (!optionMap.has(key)) {
                optionMap.set(key, {
                    code: key,
                    value: membershipName || membershipTemplateId,
                });
            }
            return;
        }

        if (!membershipName) return;

        const key = `name:${membershipName}`;
        if (!optionMap.has(key)) {
            optionMap.set(key, {
                code: key,
                value: membershipName,
            });
        }
    });

    return Array.from(optionMap.values());
};

const isMatchingMembershipTemplate = (item: MembershipPaymentData, selectedOption: string) => {
    if (selectedOption === 'all') return true;
    if (selectedOption.startsWith('id:')) {
        return item.membershipTemplateId === selectedOption.slice(3);
    }
    if (selectedOption.startsWith('name:')) {
        return item.membershipName === selectedOption.slice(5);
    }
    return item.membershipTemplateId === selectedOption;
};

/**
 * 상태 텍스트가 이미 말하고 있는 사유인가.
 *
 * 전액/부분 환불 행은 원결제 행이 아니거나 잔액이 0이라 항상 환불이 막힌다. 그 두 사유를
 * 배지로 다시 붙이면 "부분 환불 · 환불 불가"처럼 같은 말을 두 번 하고, 좁은 열에서 줄바꿈만
 * 만든다. 상태로 설명되지 않는 사유(계보 미해결·인덱스 미준비·공유 중 등)는 그대로 짚어준다.
 * 모바일 관리자 웹(M4)도 같은 이유로 환불 계열 행을 배지에서 제외한다.
 */
const isRefundBlockReasonRedundant = (saleStatus?: string, reason?: string): boolean =>
    (saleStatus === 'FULL_REFUND' || saleStatus === 'PARTIAL_REFUND') &&
    (reason === 'NOT_ORIGINAL_SALES_ROW' || reason === 'NO_REFUNDABLE_AMOUNT');

/**
 * 환불 불가 배지 + 사유 툴팁.
 *
 * `title` 속성은 쓰지 않는다 — 네이티브 툴팁은 1초 이상 지연되고 OS·브라우저에 따라 아예
 * 안 뜬다. 실제로 테스트 배포에서 "커서만 바뀌고 아무것도 안 보인다"는 피드백을 받았다.
 *
 * 상단 요약 카드의 `TooltipIcon`(SettlementCalendar)을 그대로 쓰지 못하는 이유는 위치다.
 * 그 컴포넌트는 말풍선 높이를 재서 아이콘 **위쪽**으로 밀어 올리는데, 이 표는 `overflow-auto`
 * 스크롤 컨테이너 안이라 그대로 쓰면 잘린다. 시각 언어(어두운 배경·라운드·13px 흰 글자)만
 * 가져오고 위치는 직접 정한다.
 *
 * **세로 앵커는 배지 아래쪽에 맞추고 위로 편다**(`bottom-0`). 행 중앙 정렬(`top-1/2`)이었을
 * 때는 배지가 상태 텍스트 아래에 있어 말풍선 중심이 행 중심보다 ~14px 내려갔고, 가장 긴
 * 사유(39자 → 240px 폭에서 3줄, 약 74px)에서 마지막 행이 컨테이너 아래로 ~15px 잘렸다.
 * 아래쪽 정렬이면 말풍선 하단이 배지 하단(행 바닥에서 ~12px 위)을 넘지 않아 **마지막 행에서
 * 절대 잘리지 않는다.** 반대쪽으로는 첫 행에서 행 위로 ~13px 삐져나가는데, 그 자리는 sticky
 * `<thead>` 영역이라 여전히 컨테이너 **안쪽**이다(잘리지 않는다). 말풍선이 `z-30`,
 * 헤더가 `z-20` 이라 헤더 위에 그려진다. 행 높이·글자 크기를 재는 계산은 넣지 않았다.
 *
 * 가로로는 배지(x≈35~95px) 오른쪽 8px + 240px ≈ 343px 로 표 폭(1584px) 안쪽이다.
 *
 * 클릭으로도 토글한다. 터치 기기에는 hover 가 없다. 이때 `stopPropagation` 이 필수다 —
 * 셀 전체에 행 클릭 핸들러가 걸려 있어(Table.tsx `<td onClick>`) 그냥 두면 배지나 말풍선을
 * 누르는 순간 매출 상세 모달이 같이 열린다. 말풍선 본문에도 반드시 건다.
 */
const SalesRefundBlockBadge = ({ reason }: { reason: string }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
        <div
            className="relative flex items-center"
            onMouseEnter={() => setIsOpen(true)}
            onMouseLeave={() => setIsOpen(false)}
        >
            <span
                role="button"
                tabIndex={0}
                onClick={(event) => {
                    event.stopPropagation();
                    setIsOpen((open) => !open);
                }}
                onKeyDown={(event) => {
                    if (event.key !== 'Enter' && event.key !== ' ') return;
                    event.stopPropagation();
                    event.preventDefault();
                    setIsOpen((open) => !open);
                }}
                className="inline-flex cursor-pointer items-center whitespace-nowrap rounded-[4px] border border-solid border-[#E5E7EB] bg-[#F3F4F6] px-[6px] py-[2px] text-[13px] font-semibold leading-[1.4] text-[#6B7280]"
            >
                환불 불가
            </span>
            {isOpen && (
                <div
                    onClick={(event) => event.stopPropagation()}
                    className="absolute bottom-0 left-full z-30 ml-[8px] w-[240px] rounded-[9px] bg-[#36393E] px-[12px] py-[10px] text-start shadow-lg"
                >
                    <RegularText fontSize={13} lineHeight={18} color="white" letterSpacing={-0.3}>
                        {getSalesRefundBlockMessage(reason)}
                    </RegularText>
                </div>
            )}
        </div>
    );
};

// 판매상태, 결제일시, 판매담당자, 담당강사, 회원, 결제금액, 취소액, 총 회차, 수강권, 이용기간
const columns = [
    columnHelper.accessor('saleStatus', {
        id: 'saleStatus',
        header: '판매상태',
        cell: (info) => {
            const text =
                info.getValue() === 'COMPLETED'
                    ? '완료'
                    : info.getValue() === 'UPGRADED'
                      ? '업그레이드'
                      : info.getValue() === 'PARTIAL_REFUND'
                        ? '부분 환불'
                        : info.getValue() === 'PARTIAL_PAYMENT'
                          ? '분할 결제'
                          : '전체 환불';
            const textColor =
                info.getValue() === 'COMPLETED'
                    ? '#3D4244'
                    : info.getValue() === 'UPGRADED'
                      ? '#9536D4'
                      : info.getValue() === 'PARTIAL_REFUND'
                        ? '#FF7220'
                        : info.getValue() === 'PARTIAL_PAYMENT'
                          ? '#15BA72'
                          : '#FF2020';
            /**
             * 환불 불가 사유를 목록에서 바로 보여준다(U1).
             *
             * 지금까지는 행을 열어 상세 모달까지 들어가야 "왜 환불 버튼이 비활성인지" 알 수 있었다.
             * 종료된 수강권 상세가 열리면서 환불 불가 행이 늘어나므로, 한 건씩 열어 확인하는
             * 동선이 그대로면 시간만 든다. 사유 사전은 상세 모달과 같은 것을 쓴다.
             */
            const row = info.row.original;
            const rawBlockReason = row.canRefund === false ? row.refundBlockReason : undefined;
            const blockReason = isRefundBlockReasonRedundant(info.getValue(), rawBlockReason)
                ? undefined
                : rawBlockReason;
            /**
             * 상태와 배지를 **세로로 쌓는다.** 가로로 나란히 두면 130px 열에서 "부분 환불"이
             * 배지에 밀려 글자 중간("부분 환/불")에서 꺾인다. 세로 스택은 열 폭을 늘리지 않고,
             * 배지가 없는 행은 한 줄 그대로라 다른 행과 가운데 정렬이 어긋나지 않는다.
             * `break-keep`(word-break: keep-all)은 한글이 어절 중간에서 꺾이는 것을 막는다.
             */
            return (
                <Col className="w-full items-center justify-center gap-[4px]">
                    <RegularText
                        fontSize={16}
                        lineHeight={24}
                        color={textColor}
                        className="whitespace-nowrap break-keep"
                    >
                        {text}
                    </RegularText>
                    {blockReason && <SalesRefundBlockBadge reason={blockReason} />}
                </Col>
            );
        },

        size: 130,
        enableSorting: false,
    }),
    columnHelper.accessor('paymentDate', {
        id: 'paymentDate',
        header: '결제일시',
        cell: (info) => dayjs(info.getValue()).format('YYYY-MM-DD'),
        size: 146,
        enableSorting: false,
    }),
    columnHelper.accessor('salesManager', {
        id: 'salesManager',
        header: '판매담당자',
        cell: (info) => info.getValue().userName,
        size: 138,
        enableSorting: false,
    }),

    columnHelper.accessor('memberName', {
        id: 'memberName',
        header: '회원',
        cell: (info) => {
            // 신규면 #7E7EF5 , 재등록이면 #64637B
            const bgColor = info.row.original.isNewSales ? '#7E7EF5' : '#64637B';
            const isGroup = info.row.original.source === 'group';

            return (
                <Row className="flex w-full items-center justify-center">
                    <span className="mr-[8px] w-[150px] shrink-0 truncate text-[16px] leading-[24px] tracking-[-0.32px] text-[#3D4244]" title={info.getValue()}>
                        {info.getValue()}
                    </span>
                    <span
                        className="shrink-0"
                        style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: 13,
                            fontWeight: 600,
                            padding: '2px 7px',
                            borderRadius: 4,
                            marginRight: 4,
                            backgroundColor: isGroup ? '#ECFDF5' : '#EFF6FF',
                            color: isGroup ? '#065F46' : '#1E40AF',
                        }}
                    >
                        {isGroup ? '그룹' : 'PT'}
                    </span>
                    <Col
                        className="shrink-0 justify-center items-center rounded-[16px]"
                        style={{ backgroundColor: bgColor, width: 45, height: 27 }}
                    >
                        <DemiLightText fontSize={13} lineHeight={24} color="#FFFFFF">
                            {info.row.original.isNewSales ? '신규' : '재등록'}
                        </DemiLightText>
                    </Col>
                </Row>
            );
        },
        size: 280,
        minSize: 280,
        enableSorting: false,
    }),
    columnHelper.accessor('totalPrice', {
        id: 'totalPrice',
        header: '판매금액',
        cell: (info) =>
            info.getValue() === null || info.getValue() === undefined ? '-' : convertToKRW(info.getValue()),
        size: 129,
        enableSorting: false,
    }),
    columnHelper.accessor('paymentAmount', {
        id: 'paymentAmount',
        header: '결제금액',
        cell: (info) =>
            info.getValue() === null || info.getValue() === undefined ? '-' : convertToKRW(info.getValue()),
        size: 129,
        enableSorting: false,
    }),
    columnHelper.accessor('cancelAmount', {
        id: 'cancelAmount',
        header: '취소액',
        cell: (info) =>
            info.getValue() === null || info.getValue() === undefined ? '-' : convertToKRW(info.getValue()),
        size: 129,
        enableSorting: false,
    }),
    columnHelper.accessor('totalSession', {
        id: 'totalSession',
        header: '총 회차',
        cell: (info) => {
            const row = info.row.original as any;
            // 서버가 그룹 row에 totalCount/totalCountOffset (MemberTicket 네이밍)으로 내려보내므로
            // PT 네이밍 totalSession/totalSessionOffset과 둘 다 방어적으로 읽는다.
            const totalValue = info.getValue() ?? row.totalCount;
            const offsetValue = row.totalSessionOffset ?? row.totalCountOffset ?? 0;

            const textColor =
                row.saleStatus === 'COMPLETED'
                    ? '#3D4244'
                    : row.saleStatus === 'UPGRADED'
                      ? '#9536D4'
                      : row.saleStatus === 'PARTIAL_REFUND'
                        ? '#FF7220'
                        : '#FF2020';

            //환불 , 부분환불
            const minus = row.saleStatus === 'FULL_REFUND' || row.saleStatus === 'PARTIAL_REFUND';

            // 업그이드
            const plus = row.saleStatus === 'UPGRADED';

            return (
                <>
                    <RegularText fontSize={16} lineHeight={24} color={'#3D4244'}>
                        {totalValue ?? '-'}
                    </RegularText>
                    <RegularText fontSize={16} lineHeight={24} color={textColor}>
                        {`${
                            !offsetValue
                                ? ''
                                : minus
                                  ? `(-${offsetValue})`
                                  : plus
                                    ? `(+${offsetValue})`
                                    : ''
                        }`}
                    </RegularText>
                </>
            );
        },
        size: 129,
        enableSorting: false,
    }),
    columnHelper.accessor('membershipName', {
        id: 'membershipName',
        header: '수강권',
        cell: (info) => info.getValue(),
        size: 216,
        enableSorting: false,
    }),
    columnHelper.accessor('activationDate', {
        id: 'activationDate',
        header: '이용기간',
        cell: (info) =>
            `${dayjs(info.row.original.activationDate).format('YYYY-MM-DD')} ~ ${dayjs(
                info.row.original.expirationDate,
            ).format('YYYY-MM-DD')}`,
        size: 228,
        enableSorting: false,
    }),
    columnHelper.accessor('coachName', {
        id: 'coachName',
        header: '담당강사',
        cell: (info) => info.getValue(),
        size: 129,
        enableSorting: false,
    }),
];

const SalesListPage = ({
    createAlert,
    date,
    onMonthNavigate,
    salesListData,
    selectedSalesManager,
    onSalesManagerSelect,
    selectedSalesListDate,
    onChangeSelectedSalesListDate,
    onRefetchSalesRecord,
}: Props) => {
    const [isCalendarOpen, setIsCalendarOpen] = React.useState(false);
    const [search, setSearch] = React.useState('');
    const [selectedSalesRecord, setSelectedSalesRecord] = React.useState<MembershipPaymentData | null>(null);

    const summaryData = salesListData?.summaryData;
    const tableDataByMembershipPayment = salesListData?.tableDataByMembershipPayment;
    // 서버 필터 목록에 빠진 그룹 수강권도 현재 row 기준으로 보강한다.
    const membershipTemplateData = buildMembershipTemplateOptions(
        salesListData?.filters?.membershiptemplateList,
        tableDataByMembershipPayment,
    );
    // 담당강사(판매 시점). 그 달에 PT 매출이 있는 강사만 뜬다 — 그룹 행은 제외.
    const coachFilterData = buildCoachFilterOptions(tableDataByMembershipPayment);
    // 회원
    const memberData = salesListData?.filters.memberList.map((item) => ({
        code: item.memberId,
        value: item.memberName,
    }));
    // 판매자
    const salesManagerData = salesListData?.salesManagers.map((item) => ({
        code: item.userId,
        value: item.userName,
        object: item,
    }));

    const [filteredTableData, setFilteredTableData] = React.useState(tableDataByMembershipPayment);
    const filteredCount = filteredTableData?.length;
    const filteredTotalPrice = filteredTableData?.reduce(
        (acc: any, cur: any) => acc + (Number(cur.paymentAmount) || 0) - (Number(cur.cancelAmount) || 0),
        0,
    );
    //선택된 수강권
    const [selectedMembershipTemplate, setSelectedMembershipTemplate] = React.useState('all');
    const [selectedMember, setSelectedMember] = React.useState('all');
    const [selectedMemberStatus, setSelectedMemberStatus] = React.useState('all');
    const [selectedAttendanceStatus, setSelectedAttendanceStatus] = React.useState('all');
    // 담당강사 필터(판매 시점 강사). 'all' 또는 `id:`/`name:` 키.
    const [selectedCoach, setSelectedCoach] = React.useState(COACH_FILTER_ALL);
    // Dropdown 은 표시 라벨을 내부 state 로 들고 있어 밖에서 되돌릴 수 없다. 선택을 강제로
    // '전체'로 되돌릴 때 key 를 바꿔 다시 마운트시켜 라벨도 함께 초기화한다.
    const [coachDropdownKey, setCoachDropdownKey] = React.useState(0);
    const handleSelect = (
        selected: { code: string; value: string; object?: any },
        type: 'salesManager' | 'membershipTemplate' | 'memberStatus' | 'attendanceStatus' | 'coach',
    ) => {
        if (type === 'membershipTemplate') {
            setSelectedMembershipTemplate(selected.code);
        } else if (type === 'coach') {
            setSelectedCoach(selected.code);
        } else if (type === 'salesManager') {
            onSalesManagerSelect(selected);
        } else if (type === 'memberStatus') {
            setSelectedMemberStatus(selected.code);
        } else if (type === 'attendanceStatus') {
            setSelectedAttendanceStatus(selected.code);
        }
    };

    const exportToExcel = () => {
        const data = filteredTableData;
        const localizedData = data.map((item: any) => ({
            판매상태:
                item.saleStatus === 'COMPLETED'
                    ? '완료'
                    : item.saleStatus === 'UPGRADED'
                      ? '업그레이드'
                      : item.saleStatus === 'PARTIAL_REFUND'
                        ? '부분 환불'
                        : item.saleStatus === 'PARTIAL_PAYMENT'
                          ? '분할 결제'
                          : '전체 환불',
            결제일시: dayjs(item.paymentDate).format('YYYY-MM-DD'),
            판매담당자: item.salesManager.userName,
            담당강사: item.coachName,
            회원: item.memberName,
            결제금액: convertToKRW(item.paymentAmount),
            취소액: convertToKRW(item.cancelAmount),
            총회차: (() => {
                const totalValue = item.totalSession ?? item.totalCount ?? '-';
                const offsetValue = item.totalSessionOffset ?? item.totalCountOffset ?? item.totalSessionChange ?? 0;
                const isRefund = item.saleStatus === 'FULL_REFUND' || item.saleStatus === 'PARTIAL_REFUND';
                const isUpgrade = item.saleStatus === 'UPGRADED';
                const offsetText = !offsetValue ? '' : isRefund ? `(-${offsetValue})` : isUpgrade ? `(+${offsetValue})` : '';
                return `${totalValue}회 ${offsetText}`;
            })(),
            수강권: item.membershipName,
            이용기간: `${dayjs(item.activationDate).format('YYYY-MM-DD')} ~ ${dayjs(item.expirationDate).format(
                'YYYY-MM-DD',
            )}`,
        }));
        // 1. JSON 데이터를 Sheet로 변환
        const worksheet = XLSX.utils.json_to_sheet(localizedData);
        // 2. Workbook 생성 및 Sheet 추가
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'FilteredData');
        // 3. 엑셀 파일 생성 및 다운로드
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        const blob = new Blob([excelBuffer], { type: 'application/octet-stream' });
        saveAs(blob, `${dayjs(date).format('YYYY-MM')} 매출 정산.xlsx`);
    };

    // 강사, 수강권, 출석여부 필터링
    useEffect(() => {
        if (tableDataByMembershipPayment) {
            let tempData: any = tableDataByMembershipPayment;

            // 판매상태 필터링
            if (selectedAttendanceStatus !== 'all') {
                tempData = tempData.filter(
                    (item: MembershipPaymentData) => item.saleStatus === selectedAttendanceStatus,
                );
            }
            // 수강권 필터링
            if (selectedMembershipTemplate !== 'all') {
                tempData = tempData.filter((item: MembershipPaymentData) =>
                    isMatchingMembershipTemplate(item, selectedMembershipTemplate),
                );
            }
            // 회원 구분 필터링
            if (selectedMemberStatus !== 'all') {
                tempData = tempData.filter(
                    (item: MembershipPaymentData) => item.isNewSales === (selectedMemberStatus === 'isNewSales'),
                );
            }
            // 회원 이름 필터링
            if (selectedMember !== 'all') {
                tempData = tempData.filter((item: MembershipPaymentData) => item.memberId === selectedMember);
            }
            // 담당강사 필터링 — 판매 시점 강사 기준, 그룹 행은 빠진다
            if (selectedCoach !== COACH_FILTER_ALL) {
                tempData = tempData.filter((item: MembershipPaymentData) =>
                    isMatchingCoachFilter(item, selectedCoach),
                );
            }
            // selectedSalesListDate 필터링
            if (selectedSalesListDate) {
                tempData = tempData.filter(
                    (item: MembershipPaymentData) =>
                        dayjs(item.paymentDate).format('YYYY-MM-DD') ===
                        dayjs(selectedSalesListDate).format('YYYY-MM-DD'),
                );
            }
            setFilteredTableData(tempData);
        }
    }, [
        selectedAttendanceStatus,
        selectedMembershipTemplate,
        selectedMember,
        selectedMemberStatus,
        selectedCoach,
        tableDataByMembershipPayment,
        selectedSalesListDate,
    ]);

    // 월·판매자가 바뀌어 새 데이터가 오면 고른 강사가 옵션에서 사라질 수 있다. 그대로 두면
    // 0건만 보이므로 '전체'로 되돌리고 드롭다운 라벨도 함께 초기화한다.
    useEffect(() => {
        const resolved = resolveCoachFilterSelection(selectedCoach, coachFilterData);
        if (resolved !== selectedCoach) {
            setSelectedCoach(resolved);
            setCoachDropdownKey((key) => key + 1);
        }
        // coachFilterData 는 tableDataByMembershipPayment 에서 매 렌더 새로 만들어지므로 원본을 의존성으로 쓴다.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableDataByMembershipPayment, selectedCoach]);

    return (
        <div className="flex flex-col w-full h-full pt-[29px] px-[46px] bg-white rounded-[27px] mb-[50px]">
            <Row className=" items-center mb-[40px]">
                <MediumText fontSize={25} lineHeight={25} color={'#3D4244'} className="mr-[14px]">
                    {dayjs(date).format('YYYY년 MM월')}
                </MediumText>
                <div
                    className="flex cursor-pointer bg-[#F4F5F7] w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[5px]"
                    onClick={() => {
                        onMonthNavigate(dayjs(date).subtract(1, 'month'));
                    }}
                >
                    <SvgIcon
                        name={'SvgLeftStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </div>

                <div
                    className="flex cursor-pointer bg-[#F4F5F7] w-[30px] h-[30px] justify-center items-center rounded-[4px] mr-[30px]"
                    onClick={() => {
                        onMonthNavigate(dayjs(date).add(1, 'month'));
                    }}
                >
                    <SvgIcon
                        name={'SvgRightStrokeArrow'}
                        size={15}
                        fill={colors.trans}
                        stroke={'#70747C'}
                        strokeWidth={1.2}
                    />
                </div>

                {salesManagerData && salesManagerData.length > 0 && (
                    <Dropdown
                        initialSelected={selectedSalesManager.userId}
                        options={salesManagerData}
                        onSelect={(selected) => {
                            handleSelect(selected, 'salesManager');
                        }}
                        bgColor="rgba(201, 219, 255, 0.4)"
                        textColor={colors.primary}
                        borderRadius={50}
                        totalText="판매자 전체"
                        arrowColor={colors.primary}
                        className="py-[14px]"
                        textType="medium"
                    />
                )}
                {/*
                 * 엑셀 다운로드는 상단 줄 오른쪽 끝에 둔다. 왼쪽은 "무엇을 볼지"(월·판매자), 오른쏙은
                 * "가져가기"로 역할이 나뉜다. 필터 줄에 있을 때는 담당강사 드롭다운이 추가되면서 폭이
                 * 표(1584px)를 넘길 위험이 있었다. 색은 회색을 유지해 이 줄에서 파란색은 판매자 필터
                 * 하나만 남긴다. 높이는 아래 필터 드롭다운과 같은 40px 이라 화면 리듬이 맞는다.
                 * 내보내는 데이터는 필터가 적용된 표 내용 그대로다.
                 */}
                <Row
                    className={`ml-auto h-[40px] items-center justify-center bg-[#F2F4F6] rounded-[7px] px-[16px]
                        ${
                            filteredTableData && filteredTableData.length
                                ? ' cursor-pointer'
                                : 'opacity-50 cursor-not-allowed'
                        }
                        `}
                    onClick={() => filteredTableData && filteredTableData.length && exportToExcel()}
                >
                    <SvgIcon name={'SvgExcel'} size={20} fill={colors.trans} className="mr-[7px]" />
                    <DemiLightText fontSize={16} lineHeight={23} color="#3D4244" letterSpacing={-0.32}>
                        엑셀 다운로드
                    </DemiLightText>
                </Row>
            </Row>
            <TableHeader summaryData={summaryData} />
            <Row className="flex w-full justify-between mb-[30px] items-center">
                <Row>
                    <RegularText fontSize={18} lineHeight={24} color="#3D4244" letterSpacing={-0.32}>
                        {`총 ${filteredCount}건 / 매출액 ${convertToKRW(filteredTotalPrice)}`}
                    </RegularText>
                </Row>
                <Row>
                    <button
                        className="flex flex-row relative items-center justify-center rounded-[7px] px-[12px] mr-[12px]"
                        onClick={() => setIsCalendarOpen(true)}
                        style={{
                            backgroundColor: selectedSalesListDate ? '#C9DBFF66' : '#F2F4F6',
                        }}
                    >
                        <DemiLightText
                            fontSize={17}
                            lineHeight={24.62}
                            color={selectedSalesListDate ? colors.primary : '#3D4244'}
                            className="mr-[12px]"
                        >
                            {selectedSalesListDate ? dayjs(selectedSalesListDate).format('D일') : '모든 날짜'}
                        </DemiLightText>
                        <SvgIcon
                            name={'SvgDownStrokeArrow'}
                            fill={colors.trans}
                            stroke={selectedSalesListDate ? colors.primary : '#3D4244'}
                            size={14}
                            strokeWidth={1.5}
                        />
                        {isCalendarOpen && (
                            <NewCalendar
                                date={dayjs(date)}
                                selectedDay={selectedSalesListDate}
                                setIsCalendar={setIsCalendarOpen}
                                onClickDate={(day) => {
                                    onChangeSelectedSalesListDate((day && dayjs(day)) ?? undefined);
                                }}
                            />
                        )}
                    </button>
                    <Dropdown
                        options={attendanceStatusData}
                        className="h-[40px] mr-[12px]"
                        totalText="판매 상태 전체"
                        onSelect={(selected) => {
                            handleSelect(selected, 'attendanceStatus');
                        }}
                        textType="demilight"
                        selectedBgColor="#C9DBFF66"
                        selectedTextColor={colors.primary}
                        selectedArrowColor={colors.primary}
                    />
                    <Dropdown
                        options={membershipTemplateData}
                        className="h-[40px] mr-[12px]"
                        totalText="모든 수강권"
                        onSelect={(selected) => {
                            handleSelect(selected, 'membershipTemplate');
                        }}
                        textType="demilight"
                        selectedBgColor="#C9DBFF66"
                        selectedTextColor={colors.primary}
                        selectedArrowColor={colors.primary}
                    />
                    <Dropdown
                        key={coachDropdownKey}
                        options={coachFilterData}
                        className="h-[40px] mr-[12px]"
                        totalText="담당강사 전체"
                        onSelect={(selected) => {
                            handleSelect(selected, 'coach');
                        }}
                        textType="demilight"
                        selectedBgColor="#C9DBFF66"
                        selectedTextColor={colors.primary}
                        selectedArrowColor={colors.primary}
                    />
                    <Dropdown
                        options={memberStatusData}
                        className="h-[40px] mr-[12px]"
                        totalText="회원 구분"
                        onSelect={(selected) => {
                            handleSelect(selected, 'memberStatus');
                        }}
                        textType="demilight"
                        selectedBgColor="#C9DBFF66"
                        selectedTextColor={colors.primary}
                        selectedArrowColor={colors.primary}
                    />
                    <MemberSearchTextInput
                        data={memberData}
                        width={154}
                        placeholder="회원 검색"
                        onSelect={(select) => setSearch(select.value)}
                        onSearch={(search) => {
                            setSearch(search);
                        }}
                    />
                </Row>
            </Row>
            {filteredTableData && filteredTableData.length > 0 ? (
                <Table
                    showHorizontalScrollHint
                    data={filteredTableData}
                    columns={columns}
                    width={1584}
                    globalFilter={search}
                    onGlobalFilterChange={setSearch}
                    onRowClick={(row: MembershipPaymentData) => setSelectedSalesRecord(row)}
                    className="mb-[100px]"
                    isPagination
                    customPaginationComponent={({
                        firstPage,
                        nextPage,
                        canNextPage,
                        previousPage,
                        canPreviousPage,
                    }) => (
                        <Row className=" absolute items-center flex right-0 bottom-0 mb-[50px]">
                            <RegularText
                                fontSize={16}
                                lineHeight={23}
                                color="#3D4244"
                                className={`mr-[16px] ${
                                    canPreviousPage ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                }`}
                                onClick={() => !canPreviousPage && firstPage()}
                            >
                                처음으로
                            </RegularText>
                            <button
                                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] 
                disabled:opacity-50 disabled:cursor-not-allowed
                "
                                onClick={previousPage}
                                disabled={canPreviousPage}
                            >
                                <SvgIcon
                                    name={'SvgLeftStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                    className="mr-[7px]"
                                />
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244">
                                    이전
                                </RegularText>
                            </button>
                            <button
                                className="flex flex-row items-center mr-[13px] bg-[#F4F5F7] rounded-[7px] px-[19px] py-[7px] 
                disabled:opacity-50 disabled:cursor-not-allowed
                "
                                onClick={nextPage}
                                disabled={canNextPage}
                            >
                                <RegularText fontSize={16} lineHeight={23} color="#3D4244" className="mr-[7px]">
                                    다음
                                </RegularText>
                                <SvgIcon
                                    name={'SvgRightStrokeArrow'}
                                    size={15}
                                    fill={colors.trans}
                                    stroke={'#70747C'}
                                    strokeWidth={1.2}
                                />
                            </button>
                        </Row>
                    )}
                />
            ) : (
                <Col className="flex flex-col overflow-x-auto w-full">
                    <Col className="w-full  items-center h-[500px]">
                        <MediumText
                            fontSize={20}
                            lineHeight={29}
                            letterSpacing={-0.3}
                            color="#3D4244"
                            className="mb-[8px] mt-[100px]"
                        >
                            조회 결과가 없어요
                        </MediumText>
                        <RegularText fontSize={16} lineHeight={24} color="#6B7280">
                            입력한 정보가 올바른지 확인해주세요.
                        </RegularText>
                    </Col>
                </Col>
            )}
            {selectedSalesRecord && (
                <SalesRecordDetailModal
                    record={selectedSalesRecord}
                    createAlert={createAlert}
                    onClose={() => setSelectedSalesRecord(null)}
                    onRefetchSalesRecord={() => onRefetchSalesRecord(selectedSalesRecord)}
                />
            )}
        </div>
    );
};

export default SalesListPage;

// 헤더 표
const TableHeader = ({ summaryData }: { summaryData: SalesListSummaryData }) => {
    const authObject = getItemWithExpiration('authObject');
    const isRappoPayVisible = authObject?.adminId === 'trial';
    // trial: 6열 × 190px = 1140px, 비-trial: 5열 × 228px = 1140px (총 너비 동일)
    const colWidth = isRappoPayVisible ? 'w-[190px]' : 'w-[228px]';

    const totalSales = summaryData?.totalSales ?? 0;
    const totalSalesCount = summaryData?.totalSalesCount ?? 0;
    const totalSalesMemberCount = summaryData?.totalSalesMemberCount ?? 0;
    const newPaymentAmount = summaryData?.newPaymentAmount ?? 0;
    const newPaymentCount = summaryData?.newPaymentCount ?? 0;
    const newPaymentMemberCount = summaryData?.newPaymentMemberCount ?? 0;
    const reRegistrationPaymentAmount = summaryData?.reRegistrationPaymentAmount ?? 0;
    const reRegistrationPaymentCount = summaryData?.reRegistrationPaymentCount ?? 0;
    const reRegistrationPaymentMemberCount = summaryData?.reRegistrationPaymentMemberCount ?? 0;
    const cancelSales = summaryData?.cancelSales ?? 0;
    const cancelSalesCount = summaryData?.cancelSalesCount ?? 0;
    const cancelSalesMemberCount = summaryData?.cancelSalesMemberCount ?? 0;
    const partialPaymentAmount = summaryData?.partialPaymentAmount; // 분할 결제 금액
    const partialPaymentCount = summaryData?.partialPaymentCount; // 분할 결제 건수
    const partialPaymentMemberCount = summaryData?.partialPaymentMemberCount; // 분할 결제 회원수

    return (
        <div className="w-full">
            <table className="min-w-full bg-white border border-solid border-gray-300 text-center mb-[66px]">
                <thead>
                    <tr className="bg-gray-100 border-solid border-b  h-[40px] justify-center items-center">
                        <th className={`${colWidth} h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]`}>
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    {'총 매출액'}
                                </RegularText>
                                <TooltipIcon
                                    title={`회원의 수강권 결제를 통해 발생한 총 금액`}
                                    subTitle="* 라포 서비스 내에 등록된 수강권 내역을 통해 산출된 매출 금액입니다."
                                />
                            </Row>
                        </th>
                        <th className={`${colWidth} h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]`}>
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    {'신규 결제액'}
                                </RegularText>
                                <TooltipIcon
                                    title={`처음 등록한 신규 회원이 실제로 결제한 금액`}
                                    subTitle="* 할인, 패키지 요금제, 또는 프로모션이 반영된 금액일 수 있습니다."
                                />
                            </Row>
                        </th>
                        <th className={`${colWidth} h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]`}>
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    {'재등록 결제액'}
                                </RegularText>
                                <TooltipIcon
                                    title={`기존에 서비스를 이용한 적이 있는 재등록 회원이 실제로 결제한 금액`}
                                    subTitle="* 할인, 패키지 요금제, 또는 프로모션이 반영된 금액일 수 있습니다."
                                />
                            </Row>
                        </th>
                        {isRappoPayVisible && (
                            <th className={`${colWidth} h-full justify-center items-center content-center border border-solid border-[#D9D9D9] bg-[#F0F2F3]`}>
                                <Row className="items-center justify-center">
                                    <RegularText
                                        fontSize={16}
                                        lineHeight={24}
                                        color="#6B7280"
                                        className="mr-[5px]"
                                        letterSpacing={-0.32}
                                    >
                                        {'라포페이 결제액'}
                                    </RegularText>
                                </Row>
                            </th>
                        )}
                        <th className={`${colWidth} h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]`}>
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    {'분할 결제액'}
                                </RegularText>
                            </Row>
                        </th>
                        <th className={`${colWidth} h-full justify-center items-center content-center  border border-solid border-[#D9D9D9] bg-[#F0F2F3]`}>
                            <Row className="items-center justify-center">
                                <RegularText
                                    fontSize={16}
                                    lineHeight={24}
                                    color="#6B7280"
                                    className="mr-[5px]"
                                    letterSpacing={-0.32}
                                >
                                    {'환불금액'}
                                </RegularText>
                                <TooltipIcon
                                    title={`회원이 결제한 금액 중 취소되거나 환불 요청으로 반환된 금액`}
                                    subTitle="* 결제 취소 금액과 환불된 금액이 모두 포함됩니다."
                                />
                            </Row>
                        </th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        {/* 총 매출액 */}
                        <td className=" py-[22px] border border-solid border-[#D9D9D9] ">
                            <Col>
                                <RegularText fontSize={23} lineHeight={24} color="#3D4244" className="mb-[9px]">
                                    {convertToKRW(totalSales)}
                                </RegularText>
                                <DemiLightText fontSize={15} lineHeight={24} color="#6B7280">
                                    {totalSalesCount}건/{totalSalesMemberCount}명
                                </DemiLightText>
                            </Col>
                        </td>
                        {/* 신규 결제액 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <Col>
                                <RegularText fontSize={23} lineHeight={24} color="#3D4244" className="mb-[9px]">
                                    {convertToKRW(newPaymentAmount)}
                                </RegularText>
                                <DemiLightText fontSize={15} lineHeight={24} color="#6B7280">
                                    {newPaymentCount}건/{newPaymentMemberCount}명
                                </DemiLightText>
                            </Col>
                        </td>
                        {/* 재등록 결제액 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <Col>
                                <RegularText fontSize={23} lineHeight={24} color="#3D4244" className="mb-[9px]">
                                    {convertToKRW(reRegistrationPaymentAmount)}
                                </RegularText>
                                <DemiLightText fontSize={15} lineHeight={24} color="#6B7280">
                                    {reRegistrationPaymentCount}건/{reRegistrationPaymentMemberCount}명
                                </DemiLightText>
                            </Col>
                        </td>
                        {/* 라포페이 결제액 — trial 계정만 표시 */}
                        {isRappoPayVisible && (
                            <td className="py-[22px] border border-solid border-[#D9D9D9]">
                                <Col>
                                    <RegularText fontSize={23} lineHeight={24} color="#3D4244" className="mb-[9px]">
                                        {convertToKRW(0)}
                                    </RegularText>
                                    <DemiLightText fontSize={15} lineHeight={24} color="#6B7280">
                                        0건/0명
                                    </DemiLightText>
                                </Col>
                            </td>
                        )}
                        {/* 분할 결제액 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <Col>
                                <RegularText fontSize={23} lineHeight={24} color="#3D4244" className="mb-[9px]">
                                    {convertToKRW(partialPaymentAmount)}
                                </RegularText>
                                <DemiLightText fontSize={15} lineHeight={24} color="#6B7280">
                                    {partialPaymentCount}건/{partialPaymentMemberCount}명
                                </DemiLightText>
                            </Col>
                        </td>
                        {/* 환불금액 */}
                        <td className="py-[22px] border border-solid border-[#D9D9D9]">
                            <Col>
                                <RegularText fontSize={23} lineHeight={24} color="#3D4244" className="mb-[9px]">
                                    {convertToKRW(cancelSales)}
                                </RegularText>
                                <DemiLightText fontSize={15} lineHeight={24} color="#6B7280">
                                    {cancelSalesCount}건/{cancelSalesMemberCount}명
                                </DemiLightText>
                            </Col>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    );
};
